import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Wand2, Image as ImageIcon, Loader2, Type, Camera, Settings2,
  Coins, Sparkles, Upload, Trash2, Film, Link2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { videoStudioService } from '../../services/videoStudioService';
import {
  ASPECT_RATIOS, RESOLUTIONS, GeneratedVideo, SeedanceMode, VideoCreditBalance,
  SeedanceModelInfo, SEEDANCE_MODELS_FALLBACK
} from '../../types/videoStudio';
import GalleryPicker from './GalleryPicker';
import AIPromptHelper from './AIPromptHelper';

interface Props {
  onGenerated: (video: GeneratedVideo) => void;
  balance: VideoCreditBalance | null;
  onBalanceRefresh: () => void;
  onBuyCredits: () => void;
}

// QUICK_PROMPTS: each idea has a translation KEY for the label (translated per
// locale) and a fixed English prompt body (Seedance accepts any language but the
// proven-good wording is in English here for the canned ideas).
const QUICK_PROMPTS = [
  { labelKey: 'videoStudio.scenes.quickPrompts.productRotating', prompt: 'A premium product slowly rotating 360 degrees on a soft white pedestal, cinematic studio lighting, soft shadows, 4k commercial photography' },
  { labelKey: 'videoStudio.scenes.quickPrompts.urbanScene',      prompt: 'Bustling modern city street at golden hour, people walking, soft bokeh, cinematic anamorphic lens, warm tones' },
  { labelKey: 'videoStudio.scenes.quickPrompts.sunsetSea',       prompt: 'Aerial drone shot flying over calm ocean waves at sunset, golden hour, slow forward movement, cinematic color grading' },
  { labelKey: 'videoStudio.scenes.quickPrompts.handsUnboxing',   prompt: 'Close-up of hands carefully unboxing an elegant package, satisfying reveal, soft natural light, advertising commercial style' }
];

const SeedanceGenerator: React.FC<Props> = ({ onGenerated, balance, onBalanceRefresh, onBuyCredits }) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState<SeedanceMode>('text2video');

  // Modelos vêm do backend (/providers/status) — o fallback só cobre o
  // intervalo entre o mount e a resposta.
  const [models, setModels] = useState<SeedanceModelInfo[]>(SEEDANCE_MODELS_FALLBACK);
  const [modelId, setModelIdState] = useState<string>(() => {
    try { return localStorage.getItem('sd_model') || 'seedance-2.5'; } catch { return 'seedance-2.5'; }
  });
  const model = models.find(m => m.id === modelId) || models[0];
  const setModelId = (v: string) => {
    setModelIdState(v);
    try { localStorage.setItem('sd_model', v); } catch {}
  };

  // Cada modelo tem um teto próprio de keyframes (2.5 aceita mais referências
  // que o 2.0). Sem isso mandávamos 5 imagens para um modelo que recusa.
  const MAX_IMAGES = model?.max_images ?? 5;
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  // Convenience accessor: the first slot doubles as the "image_url" backward-compat field
  const imageUrl = imageUrls[0] || null;
  // Persist Seedance settings — users tend to stick with the same format/quality.
  const [duration, setDurationState] = useState<number>(() => {
    try { const v = parseInt(localStorage.getItem('sd_duration') || '', 10); return isNaN(v) ? 5 : v; } catch { return 5; }
  });
  const [resolution, setResolutionState] = useState<'480p' | '720p' | '1080p' | '4k'>(() => {
    try { return (localStorage.getItem('sd_resolution') as any) || '720p'; } catch { return '720p'; }
  });
  const [aspectRatio, setAspectRatioState] = useState<'16:9' | '9:16' | '1:1' | '4:3'>(() => {
    try { return (localStorage.getItem('sd_aspect_ratio') as any) || '16:9'; } catch { return '16:9'; }
  });
  const setDuration = (v: number) => {
    setDurationState(v);
    try { localStorage.setItem('sd_duration', String(v)); } catch {}
  };
  const setResolution = (v: '480p' | '720p' | '1080p' | '4k') => {
    setResolutionState(v);
    try { localStorage.setItem('sd_resolution', v); } catch {}
  };
  const setAspectRatio = (v: '16:9' | '9:16' | '1:1' | '4:3') => {
    setAspectRatioState(v);
    try { localStorage.setItem('sd_aspect_ratio', v); } catch {}
  };
  const [cameraFixed, setCameraFixed] = useState(false);

  const [estimatedCost, setEstimatedCost] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState<string>('');
  // Library picker for "use last frame of an existing video as the seed"
  const [showVideoLibrary, setShowVideoLibrary] = useState(false);
  const [extractingFrame, setExtractingFrame] = useState(false);

  // Lista real de modelos + default do servidor
  useEffect(() => {
    let active = true;
    videoStudioService.providerStatus().then(r => {
      const sd = r?.data?.seedance;
      if (!active || !sd?.models?.length) return;
      setModels(sd.models);
      const known = sd.models.some(m => m.id === modelId);
      if (!known) setModelIdState(sd.default_model || sd.models[0].id);
    }).catch(() => { /* mantém o fallback */ });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Troca de modelo: encaixa duração/resolução/keyframes nos limites do novo
  // modelo (2.5 vai a 30s, o 2.0 para em 15s; o Fast não tem 1080p).
  useEffect(() => {
    if (!model) return;
    if (duration > model.max_duration) setDuration(model.max_duration);
    else if (duration < model.min_duration) setDuration(model.min_duration);
    if (!model.resolutions.includes(resolution)) setResolution('720p');
    setImageUrls(prev => (prev.length > model.max_images ? prev.slice(0, model.max_images) : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, model?.max_duration, model?.min_duration]);

  // Live cost estimation as the user tweaks parameters
  useEffect(() => {
    let active = true;
    const timer = setTimeout(() => {
      videoStudioService.estimate({
        provider: 'seedance',
        mode,
        parameters: { duration, resolution, model: modelId }
      }).then(r => {
        if (active && r.success && r.data) setEstimatedCost(r.data.cost);
      }).catch(() => { /* silent */ });
    }, 200);
    return () => { active = false; clearTimeout(timer); };
  }, [mode, duration, resolution, modelId]);

  // PiAPI / Seedance image input limits (doc da PiAPI; o teto por modelo vem de MAX_IMAGES):
  //   • formats: jpg, jpeg, png, webp
  //   • max 10 MB per image
  //   • recommended ≥720p so the model has detail to work with
  const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
  const ALLOWED_IMAGE_EXT = /^image\/(jpe?g|png|webp)$/i;

  const validateImageFile = (file: File): string | null => {
    if (!ALLOWED_IMAGE_EXT.test(file.type)) return t('videoStudio.scenes.imageBadFormat', 'Formato não suportado — use JPG, PNG ou WebP') as string;
    if (file.size > MAX_IMAGE_BYTES) return t('videoStudio.scenes.imageTooLarge', 'Imagem maior que 10MB — comprima antes') as string;
    return null;
  };

  // Append a new keyframe to the imageUrls array (until MAX_IMAGES is reached).
  // `replaceIndex` lets the caller swap a specific slot instead of appending.
  const handleImageUpload = async (file: File, replaceIndex?: number) => {
    const err = validateImageFile(file);
    if (err) { toast.error(err); return; }
    if (replaceIndex === undefined && imageUrls.length >= MAX_IMAGES) {
      toast.error(t('videoStudio.scenes.maxKeyframes', `Máximo ${MAX_IMAGES} keyframes`, { count: MAX_IMAGES }) as string);
      return;
    }
    try {
      const r = await videoStudioService.uploadImage(file);
      setImageUrls(prev => {
        if (replaceIndex !== undefined && replaceIndex < prev.length) {
          const copy = [...prev]; copy[replaceIndex] = r.url; return copy;
        }
        return [...prev, r.url];
      });
      setMode('image2video');
      toast.success(t('videoStudio.scenes.imageLoaded'));
    } catch (e: any) {
      toast.error(e.message || t('videoStudio.scenes.uploadFail'));
    }
  };

  const removeImageAt = (index: number) => {
    setImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const insufficient = balance && estimatedCost !== null && balance.available < estimatedCost;

  // Grab the LAST frame of a video URL → upload it as a still image → use as
  // image2video seed. Lets the user "continue" from where a previous AI clip
  // left off, so two clips visually concatenate without a hard cut.
  const useLastFrameOfVideo = async (videoUrl: string, label: string) => {
    setExtractingFrame(true);
    try {
      // Route S3 through our proxy so canvas.drawImage doesn't choke on CORS
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('token') || '';
      const safeUrl = /amazonaws\.com\//i.test(videoUrl)
        ? `${apiBase}/video-studio/proxy?url=${encodeURIComponent(videoUrl)}&token=${encodeURIComponent(token)}`
        : videoUrl;

      const blob = await new Promise<Blob>((resolve, reject) => {
        const v = document.createElement('video');
        v.crossOrigin = 'anonymous';
        v.src = safeUrl;
        v.muted = true; v.playsInline = true;
        v.onerror = () => reject(new Error('Falha ao carregar o vídeo'));
        v.onloadedmetadata = () => {
          // Seek a hair before the end — exactly `duration` often resolves to a black frame
          const target = Math.max(0, v.duration - 0.05);
          v.currentTime = target;
        };
        v.onseeked = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = v.videoWidth || 1280;
            canvas.height = v.videoHeight || 720;
            const ctx = canvas.getContext('2d');
            if (!ctx) return reject(new Error('Canvas indisponível'));
            ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob falhou')), 'image/jpeg', 0.92);
          } catch (e: any) { reject(e); }
        };
      });

      const file = new File([blob], `last-frame-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const r = await videoStudioService.uploadImage(file);
      // Replace slot 0 (first frame) with the extracted last-frame of the chosen video
      setImageUrls([r.url]);
      setMode('image2video');
      toast.success(t('videoStudio.seedance.frameReady', { name: label }) as string);
    } catch (e: any) {
      toast.error(e?.message || (t('videoStudio.seedance.frameFailed') as string));
    } finally {
      setExtractingFrame(false);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error(t('videoStudio.scenes.errorPrompt'));
      return;
    }
    if (mode === 'image2video' && !imageUrl) {
      toast.error(t('videoStudio.scenes.errorImage'));
      return;
    }
    if (insufficient) {
      toast.error(t('videoStudio.credits.insufficientHint'));
      onBuyCredits();
      return;
    }

    setGenerating(true);
    setProgress(0);
    setProgressLabel(t('videoStudio.scenes.progress.submitting'));

    try {
      const res = await videoStudioService.generate(
        {
          provider: 'seedance',
          prompt,
          mode,
          aspect_ratio: aspectRatio as any,
          parameters: {
            duration,
            resolution: resolution as any,
            model: modelId,
            image_url: imageUrls[0] || undefined,
            // Multi-keyframe input — Seedance interpolates motion through them in order
            image_urls: imageUrls.length > 1 ? imageUrls : undefined,
            camera_fixed: cameraFixed
          }
        },
        (pct) => {
          setProgress(pct);
          if (pct < 20) setProgressLabel(t('videoStudio.scenes.progress.accepting'));
          else if (pct < 60) setProgressLabel(t('videoStudio.scenes.progress.framing'));
          else if (pct < 90) setProgressLabel(t('videoStudio.scenes.progress.rendering'));
          else setProgressLabel(t('videoStudio.scenes.progress.saving'));
        }
      );

      if (res.success && res.data) {
        onGenerated(res.data);
        toast.success(t('videoStudio.scenes.successGenerated'));
        onBalanceRefresh();
        setProgress(100);
      }
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || t('videoStudio.scenes.errorPrompt');
      if (e.response?.status === 402 || e.response?.data?.code === 'INSUFFICIENT_VIDEO_CREDITS') {
        toast.error(t('videoStudio.credits.insufficient'));
        onBuyCredits();
      } else {
        toast.error(msg);
      }
    } finally {
      setGenerating(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header com o modelo ativo */}
      <div className="flex items-center justify-between gap-2 pb-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-700 dark:text-cyan-300 border border-cyan-500/40">
            <Sparkles className="w-2.5 h-2.5" />
            {model?.label || 'Seedance 2.5'}
          </span>
          <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">{t('videoStudio.scenes.audioSync')}</span>
        </div>
        <span className="text-[10px] text-gray-500">{t('videoStudio.scenes.generationTime')}</span>
      </div>

      {/* Prompt */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
          <Type className="w-3.5 h-3.5" />
          {t('videoStudio.scenes.describeLabel')}
        </label>
        {/* Brand-aware AI helper ABOVE the textarea — user describes intent, AI writes the prompt */}
        <div className="mb-2">
          <AIPromptHelper engine="seedance" currentText={prompt} onApply={setPrompt} />
        </div>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={t('videoStudio.scenes.describePlaceholder')}
          rows={4}
          className="w-full px-4 py-3 rounded-xl bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:border-primary-500/50 focus:ring-2 focus:ring-primary-500/20 transition resize-none"
        />
        <div className="flex flex-wrap gap-1.5 mt-2">
          {QUICK_PROMPTS.map(q => (
            <button
              key={q.labelKey}
              onClick={() => setPrompt(q.prompt)}
              className="text-[11px] px-2 py-1 rounded-md bg-white/5 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-800 hover:bg-gray-200 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white transition"
            >
              <Sparkles className="w-3 h-3 inline mr-1" />
              {t(q.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Mode tabs */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => { setMode('text2video'); setImageUrls([]); }}
          className={`p-3 rounded-xl border text-left transition ${
            mode === 'text2video'
              ? 'bg-primary-500/10 border-primary-500/50 text-gray-900 dark:text-white'
              : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:border-gray-700'
          }`}
        >
          <Type className="w-4 h-4 mb-1" />
          <p className="font-semibold text-sm">{t('videoStudio.scenes.modeText')}</p>
          <p className="text-[11px] text-gray-500">{t('videoStudio.scenes.modeTextDesc')}</p>
        </button>
        <button
          onClick={() => setMode('image2video')}
          className={`p-3 rounded-xl border text-left transition ${
            mode === 'image2video'
              ? 'bg-primary-500/10 border-primary-500/50 text-gray-900 dark:text-white'
              : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:border-gray-700'
          }`}
        >
          <ImageIcon className="w-4 h-4 mb-1" />
          <p className="font-semibold text-sm">{t('videoStudio.scenes.modeImage')}</p>
          <p className="text-[11px] text-gray-500">{t('videoStudio.scenes.modeImageDesc')}</p>
        </button>
      </div>

      {/* Image upload (image2video) — Seedance 2.0 accepts up to 5 ordered keyframes.
          The model interpolates motion through them in sequence (1st → 2nd → ... → Nth). */}
      {mode === 'image2video' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              {t('videoStudio.scenes.keyframes', 'Keyframes ({{count}}/{{max}})', { count: imageUrls.length, max: MAX_IMAGES })}
            </label>
            <span className="text-[10px] text-gray-500">
              {t('videoStudio.scenes.keyframesHint', 'Seedance interpola na ordem · JPG/PNG/WebP até 10MB cada')}
            </span>
          </div>
          <div className={`grid gap-2 ${imageUrls.length === 0 ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3'}`}>
            {imageUrls.map((url, idx) => (
              <FrameSlot
                key={idx}
                url={url}
                label={t('videoStudio.scenes.frameN', 'Frame {{n}}', { n: idx + 1 }) as string}
                hint={
                  idx === 0
                    ? (t('videoStudio.scenes.firstFrameHint', 'Inicial') as string)
                    : idx === imageUrls.length - 1
                      ? (t('videoStudio.scenes.currentLastHint', 'Último (até agora)') as string)
                      : (t('videoStudio.scenes.middleHint', 'Intermediário') as string)
                }
                accept={t('videoStudio.scenes.uploadCta') as string}
                accentClass={idx === 0 ? 'border-primary-500/50' : 'border-fuchsia-500/40'}
                onClear={() => removeImageAt(idx)}
                onPick={(file) => handleImageUpload(file, idx)}
              />
            ))}
            {/* Add-frame slot — only shown while we're under the cap */}
            {imageUrls.length < MAX_IMAGES && (
              <FrameSlot
                url={null}
                label={
                  imageUrls.length === 0
                    ? (t('videoStudio.scenes.addFirstFrame', 'Adicionar frame inicial') as string)
                    : (t('videoStudio.scenes.addNextFrame', 'Adicionar próximo keyframe') as string)
                }
                hint={
                  imageUrls.length === 0
                    ? (t('videoStudio.scenes.firstFrameHint', 'Obrigatório · JPG/PNG/WebP até 10MB') as string)
                    : (t('videoStudio.scenes.optionalHint', 'Opcional · até 5 no total') as string)
                }
                accept={t('videoStudio.scenes.uploadCta') as string}
                accentClass="border-gray-300 dark:border-gray-700 border-dashed"
                onClear={() => {}}
                onPick={(file) => handleImageUpload(file)}
              />
            )}
          </div>
          {imageUrls.length >= 2 && (
            <p className="text-[10px] text-fuchsia-700 dark:text-fuchsia-300 text-center">
              {t('videoStudio.scenes.interpolationOn', 'Modo multi-keyframe ativo: Seedance vai animar a transição entre {{n}} imagens.', { n: imageUrls.length })}
            </p>
          )}

          {/* Continuation seed: pick a video from the library and use its last frame.
              Great for chaining two AI clips without a visible cut. */}
          <button
            type="button"
            onClick={() => setShowVideoLibrary(true)}
            disabled={extractingFrame}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-700 dark:text-cyan-300 text-xs font-semibold hover:bg-cyan-500/20 transition disabled:opacity-50"
          >
            {extractingFrame ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Extraindo último frame…</>
            ) : (
              <>
                <Link2 className="w-3.5 h-3.5" />
                <span>Continuar de um vídeo da biblioteca</span>
                <Film className="w-3.5 h-3.5 opacity-60" />
              </>
            )}
          </button>
          <p className="text-[10px] text-gray-500 -mt-1 text-center">
            Pega o último frame do vídeo escolhido pra a IA continuar a partir dele.
          </p>
        </div>
      )}

      {/* Library picker — only for image2video continuation */}
      <GalleryPicker
        open={showVideoLibrary}
        source="video"
        onClose={() => setShowVideoLibrary(false)}
        onPick={(asset) => {
          setShowVideoLibrary(false);
          if (asset.url) useLastFrameOfVideo(asset.url, asset.title || 'vídeo');
          else toast.error('Vídeo sem URL disponível');
        }}
      />

      {/* Settings grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            {t('videoStudio.scenes.model', 'Modelo')}
          </label>
          <select
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500/50"
          >
            {models.map(m => (
              <option key={m.id} value={m.id}>
                {m.label}
                {m.recommended ? ` — ${t('videoStudio.scenes.modelRecommended', 'recomendado')}` : ''}
                {` · ${t('videoStudio.scenes.modelUpTo', 'até')} ${m.max_duration}s`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            {t('videoStudio.scenes.ratio')}
          </label>
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value as any)}
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500/50"
          >
            {ASPECT_RATIOS.map(a => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            {t('videoStudio.scenes.resolution')}
          </label>
          <select
            value={resolution}
            onChange={(e) => setResolution(e.target.value as any)}
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500/50"
          >
            {RESOLUTIONS.filter(r => !model || model.resolutions.includes(r.value)).map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            {t('videoStudio.scenes.duration')}
          </label>
          <select
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500/50"
          >
            {/* A API recusa menos que min_duration e corta acima de max_duration —
                o seletor só mostra o que o modelo aceita de verdade. */}
            {Array.from(
              { length: (model?.max_duration ?? 15) - (model?.min_duration ?? 4) + 1 },
              (_, i) => (model?.min_duration ?? 4) + i
            ).map(d => (
              <option key={d} value={d}>{d}s</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            <Camera className="w-3 h-3 inline mr-1" />
            {t('videoStudio.scenes.camera')}
          </label>
          <button
            onClick={() => setCameraFixed(!cameraFixed)}
            className={`w-full py-2 rounded-lg text-sm font-semibold border transition ${
              cameraFixed
                ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white'
                : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400'
            }`}
          >
            {cameraFixed ? t('videoStudio.scenes.cameraFixed') : t('videoStudio.scenes.cameraFree')}
          </button>
        </div>
      </div>

      {/* Cost preview + CTA — bg is always dark, so text needs to be white in light mode too */}
      <div className="rounded-xl bg-gradient-to-br from-gray-900/80 to-gray-950 border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-gray-300 text-xs uppercase tracking-wider font-semibold">
            <Coins className="w-3.5 h-3.5" />
            {t('videoStudio.credits.estimatedCost')}
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-white">{estimatedCost ?? '—'}</span>
            <span className="text-xs text-gray-400">{t('videoStudio.credits.creditsUnit')}</span>
          </div>
        </div>
        {progress > 0 && generating && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-600 dark:text-gray-400">{progressLabel}</span>
              <span className="text-xs text-gray-700 dark:text-gray-300 tabular-nums">{progress}%</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-gray-800 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-purple-500 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
        <button
          onClick={handleGenerate}
          disabled={generating || !prompt.trim()}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-primary-500 to-purple-600 text-white font-semibold flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> {t('videoStudio.scenes.btnGenerating')}</>
          ) : (
            <><Wand2 className="w-4 h-4" /> {t('videoStudio.scenes.btnGenerate')}</>
          )}
        </button>
        {insufficient && !generating && (
          <p className="text-xs text-rose-400 mt-2 text-center">
            Você não tem {t('videoStudio.credits.creditsUnit')} suficientes —
            <button onClick={onBuyCredits} className="underline ml-1">comprar agora</button>
          </p>
        )}
      </div>
    </div>
  );
};

// Reusable image-slot tile — same look for "first frame" and "last frame".
const FrameSlot: React.FC<{
  url: string | null;
  label: string;
  hint: string;
  accept: string;
  accentClass: string;
  onClear: () => void;
  onPick: (file: File) => void;
}> = ({ url, label, hint, accept, accentClass, onClear, onPick }) => {
  if (url) {
    return (
      <div className="relative rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 group">
        <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-black/70 text-white z-10">
          {label}
        </div>
        <img src={url} alt={label} className="w-full max-h-56 object-contain bg-gray-950" />
        <button
          onClick={onClear}
          className="absolute top-1 right-1 p-1.5 rounded-lg bg-white dark:bg-gray-900/80 text-gray-700 dark:text-gray-300 opacity-0 group-hover:opacity-100 transition z-10"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  return (
    <label className={`flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed ${accentClass} hover:bg-primary-500/5 cursor-pointer transition min-h-[140px]`}>
      <Upload className="w-5 h-5 text-gray-500 mb-1.5" />
      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">{label}</p>
      <p className="text-[10px] text-gray-500 mt-0.5 text-center">{hint}</p>
      <p className="text-[10px] text-gray-600 mt-1.5">{accept}</p>
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])}
      />
    </label>
  );
};

export default SeedanceGenerator;
